import { Injectable } from '@angular/core';
import posthog from 'posthog-js';

import { AddonItem, BasketPriceInfoCode, OrderAddon } from '../booking-api/client';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  public captureOrderBegin(data: { guests?: number; sessionId?: string; length?: number }) {
    this.capture('start_booking', {
      guests: data.guests,
      sessionId: data.sessionId,
      length: data.length
    });
  }

  public captureOrderSubmit(data: AnalyticsPurchaseData, metadata: AnalyticsPurchaseMetadata) {
    this.capture('submit_order', data);
  }

  public captureOrderCancel(data: { fee?: number; hoursToBegin: number; dayOfWeek: string; hour: string }) {
    this.capture('cancel_order', {
      fee: data.fee,
      hoursToBegin: data.hoursToBegin,
      dayOfWeek: data.dayOfWeek,
      hour: data.hour
    });
  }

  public captureOrderError() {
    this.capture('order_error');
  }

  public captureSessionSelect(data: { locationId: string; sessionId: string; length: number }) {
    this.capture('session_select', {
      locationId: data.locationId,
      sessionId: data.sessionId,
      length: data.length
    });
  }

  public captureSessionLocked(sessionId: string, lockId: string) {
    this.capture('session_locked', { sessionId, lockId });
  }

  public captureSessionLockExpired() {
    this.capture('lock_expired');
  }

  public captureSessionAddonAdd(data: { addon: AddonItem }) {
    this.capture('addon_add', data.addon);
  }

  public captureInsuranceOpen() {
    this.capture('insurance_open');
  }

  public captureInsuranceConfirm(data: { action: 'accept' | 'denied' }) {
    this.capture('insurance_confirm', { action: data.action });
  }

  public captureAccountCreate(data: { email: string }) {
    this.capture('account_created', { email: data.email.trim().toLowerCase() });
  }

  public captureAccountCreateFailed(data: { email: string }) {
    this.capture('account_creation_failed', { email: data.email.trim().toLowerCase() });
  }

  public captureAccountLogin(email: string) {
    this.identify(email, { email });
    this.capture('login');
  }

  public captureAccountLogout(data: { silent: boolean }) {
    this.capture('logout', { silent: data.silent });
    this.reset();
  }

  public captureAccountLoginFail(email: string, error?: string) {
    this.capture('login_failed', { email, error });
  }

  public captureAccountLoginInlineSuccess() {
    this.capture('inline_login_success');
  }

  public captureAccountLoginInlineFail(data: { error: unknown }) {
    this.capture('inline_login_failed', { error: data.error });
  }

  public captureAccountLogoutInline() {
    this.capture('inline_logout');
    this.reset();
  }

  public captureAccountPasswordRecoveryRequest(data: { email: string }) {
    this.capture('password_recovery_requested', { email: data.email });
  }

  public captureAccountPasswordRecoveryFail(data: { email: string; error: unknown }) {
    this.capture('password_recovery_failed', { email: data.email, error: data.error });
  }

  public captureAccountPasswordResetSuccess() {
    this.capture('password_reset_success');
  }

  public captureAccountPasswordResetFail(data: { error: 'PASSWORD_MISMATCH' | 'UNKNOWN_ERROR' }) {
    this.capture('password_reset_failed', { error: data.error });
  }

  public captureAccountPasswordChangeSuccess() {
    this.capture('password_change_success');
  }

  public captureAccountPasswordChangeFail(data: { error: unknown }) {
    this.capture('password_change_failed', { error: data.error });
  }

  public captureAccountEmailChangeSuccess() {
    this.capture('email_change_success');
  }

  public captureAccountEmailChangeFail() {
    this.capture('email_change_failed');
  }

  public captureAccountEmailVerifySuccess() {
    this.capture('email_verified');
  }

  public captureAccountEmailVerifyFail() {
    this.capture('email_verify_failed');
  }

  public captureAccountProfileUpdateSuccess() {
    this.capture('profile_updated');
  }

  public captureAccountProfileUpdateFail() {
    this.capture('profile_update_failed');
  }

  public captureAccountPresetSaveSuccess(data: { saunaTemperature: number; mood: string }) {
    this.capture('preset_saved', {
      saunaTemperature: data.saunaTemperature,
      mood: data.mood
    });
  }

  public captureAccountPresetSaveFail() {
    this.capture('preset_save_failed');
  }

  public captureGiftCardCodeRedeem() {
    this.capture('code_redeem');
  }

  public captureGiftCardCodeInvalid(data: { code: BasketPriceInfoCode }) {
    this.capture('code_invalid', { code: data.code });
  }

  private capture(event: string, properties?: Record<string, any>) {
    posthog.capture(event, properties);
  }

  private identify(distinctId: string, properties?: Record<string, any>) {
    posthog.identify(distinctId, properties);
  }

  private reset() {
    posthog.reset();
  }
}

export interface AnalyticsPurchaseData {
  total: number;
  orderNumber: string;
  session: {
    day: string;
    time: string;
    length: number;
  };
  guestCount: number;
  addons: OrderAddon[];
}

export interface AnalyticsPurchaseMetadata {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  birthday: string;
  gender: string;
  city: string;
  zip: string;
  country: string;
  sessionStart: Date;
}
